(function() {

  // custom buybox namespace
  const ns = 'staylayout5';

  const SELECTOR = {
    gwpContainer: `.${ns}-gwp-container`,
    stayaiCustomBuybox: '[data-stayai-custom-buybox-id]',
    shopifyForm: 'product-form.product-form form.form[action=\'/cart/add\'][data-type=\'add-to-cart-form\'][method=\'post\']',
    reploForm: '[data-replo-product-handle] product-form form[action=\'/cart/add\'][data-productid][data-type=\'add-to-cart-form\'][method=\'post\']',

    /** Custom buybox container (re-queries DOM on each access). */
    get stayaiCustomBuyboxEl() {
      return document.querySelector(this.stayaiCustomBuybox);
    },
    /** GWP container element (re-queries DOM on each access; use when the element may not exist yet or can be re-inserted). */
    get gwpContainerEl() {
      return document.querySelector(this.stayaiCustomBuybox + " " + this.gwpContainer);
    },
  };

  // Rebuy
  const REBUY_GWP_WIDGET_ID_DEFAULT = '228175';
  const REBUY_DATA_SOURCE_ID_DEFAULT = '238068';

  // Lazy-read from DOM when needed to avoid query at script load
  function getRebuyWidgetId(el) {
    return (el?.getAttribute('data-stayai-rebuy-gwp-id')?.trim()) || REBUY_GWP_WIDGET_ID_DEFAULT;
  }
  function getRebuyDataSourceId(el) {
    return (el?.getAttribute('data-stayai-rebuy-gwp-datasource-id')?.trim()) || REBUY_DATA_SOURCE_ID_DEFAULT;
  }

  const REBUY_API_KEY = 'b6152a007d871a29d0857588140bc01e8788a4a5';
  const RETRY_DELAY_MS = 1000;
  const RETRY_MAX = 8;

  /**
   * Returns true if the Rebuy widget is ready for GWP: status is 'ready' and metadata
   * has at least one rule in matched_rules or unmatched_rules (same as buildVariantToGiftMap uses).
   */
  function isRebuyWidgetReady(widget) {
    if (!widget || widget.status !== 'ready') return false;
    const metadata = widget.data?.metadata;
    if (!metadata) return false;
    const hasMatched = (metadata.matched_rules?.length ?? 0) > 0;
    const hasUnmatched = (metadata.unmatched_rules?.length ?? 0) > 0;
    return hasMatched || hasUnmatched;
  }

  /**
   * Resolves with the Rebuy GWP widget when window.Rebuy.widgets and metadata are ready.
   * Rejects after RETRY_MAX attempts.
   * @param {string} targetedWidgetId - Rebuy widget ID to wait for (e.g. '228175')
   * Use before buildVariantToGiftMap(w) or any logic that needs widget.data.metadata.
   */
  function waitForRebuyWidgetReady(targetedWidgetId) {
    const widgetId = (targetedWidgetId != null && targetedWidgetId !== '') ? String(targetedWidgetId) : REBUY_GWP_WIDGET_ID_DEFAULT;
    return new Promise((resolve, reject) => {
      let attempts = 0;
      function tick() {
        const widgets = window.Rebuy?.widgets;
        if (Array.isArray(widgets)) {
          const w = widgets.find(x => String(x.id) === widgetId);
          if (isRebuyWidgetReady(w)) {
            resolve(w);
            return;
          }
        }
        attempts++;
        if (attempts >= RETRY_MAX) {
          reject(new Error('Could not get rebuy GWP widget ' + widgetId + ' ready after ' + RETRY_MAX + ' attempts'));
          clearGwpContainer(null);
          return;
        }
        setTimeout(tick, RETRY_DELAY_MS);
      }
      tick();
    });
  }



  // Optional container avoids re-query when caller already has it (e.g. from main)
  function clearGwpContainer(containerEl) {
    const el = containerEl ?? SELECTOR.gwpContainerEl;
    if (el) el.innerHTML = '';
  }

  // helper: get the gifts product ids for the selected variant
  function giftsForVariant(gwpData, variantId) {
    const keyStr = String(variantId);
    const keyNum = Number(variantId);
    return gwpData[keyStr] ?? gwpData[keyNum] ?? [];
  }

  // build the variant gift map from the widget data
  function buildVariantToGiftMap(widget, targetedWidgetId) {
    const meta = widget?.data?.metadata;
    const rules = [
      ...(meta?.matched_rules ?? []),
      ...(meta?.unmatched_rules ?? []),
    ];

    const map = new Map(); // variantId -> Set(giftProductId)

    for (const rule of rules) {
      // gifts
      const giftIds = (rule.output ?? [])
        .filter(o => o.type === "product")
        .flatMap(o => o.products ?? [])
        .map(p => String(p.product_id));

      // qualifying variant ids (may be nested by rule type)
      const qualifyingVariantIds = (rule.logic ?? [])
        .flatMap(l => l.rules ?? [])
        .flatMap(r => r.products ?? [])
        .flatMap(p => p.variant_ids ?? [])
        .map(String);

      for (const vId of qualifyingVariantIds) {
        if (!map.has(vId)) map.set(vId, new Set());
        const set = map.get(vId);
        for (const g of giftIds) set.add(g);
      }
    }


    // to plain object (single object build, no spread)
    const out = Object.fromEntries([...map.entries()].map(([k, set]) => [k, [...set]]));
    out.discount = widget.data.config.discount;
    out.widgetType = widget.data.config.type;
    return out;
  }

  /**
   * Returns a thumbnail image URL (smaller file) when possible.
   * Shopify CDN: rewrites URL to request maxWidth px (e.g. image.jpg → image_100x.jpg).
   * Other URLs: returned as-is; use width/loading on img for display efficiency.
   */
  function thumbnailImageUrl(url, maxWidth = 100) {
    if (!url || typeof url !== 'string') return '';
    if (!url.includes('cdn.shopify.com')) return url;
    return url.replace(/(\.(jpe?g|png|gif|webp))(\?|#|$)/i, '_' + maxWidth + 'x$1$3');
  }

  // rebuy api for fetching gift products
  async function fetchGiftProducts({ rebuyKey, giftProductIds }) {
    if (!giftProductIds.length) return [];
    const url = new URL("https://rebuyengine.com/api/v1/products/static");
    url.searchParams.set("key", rebuyKey);
    url.searchParams.set("shopify_product_ids", giftProductIds.join(","));

    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Static products failed: ${res.status}`);
    const json = await res.json();
    return json?.data ?? json; // depending on payload shape
  }

  // rebuy api for fetching Data Source
  // usage
  // prefetchGwpProducts({
  //   rulesetId: REBUY_DATA_SOURCE_ID,
  //   rebuyPublicKey: REBUY_API_KEY,
  //   qualifyingVariantId: 46138085507249,
  // }).then((data) => {
  //   console.log(">>> rebuy response of ruleset data source:", data);
  // });
  async function prefetchGwpProducts({ DataSourceId, rebuyPublicKey, qualifyingVariantId }) {
    const url = new URL(`https://rebuyengine.com/api/v1/custom/id/${DataSourceId}`);
    url.searchParams.set("key", rebuyPublicKey);
    url.searchParams.set("shopify_variant_ids", String(qualifyingVariantId));

    const res = await fetch(url.toString(), { credentials: "omit" });
    if (!res.ok) throw new Error(`Rebuy custom endpoint failed: ${res.status}`);
    const data = await res.json();

    // Returned products are typically in data.data (and/or data.products depending on ruleset config/version)
    return data;
  }

/**
 * Run when Rebuy global object is ready
 */
  let mainHasRun = false;
  async function main(targetedWidgetId) {
    if (mainHasRun) return;
    try {
      // Check buybox first (sync); only wait for Replo container if buybox not found (avoids blocking)
      const stayaiCustomBuyboxEl = SELECTOR.stayaiCustomBuyboxEl;
      if (stayaiCustomBuyboxEl) {
        console.log('>>> stayaiCustomBuyboxEl found');
      }else{
        console.log('>>> stayaiCustomBuyboxEl not found');
      }
      let reploGwpEl = null;
      if (!stayaiCustomBuyboxEl) {
        reploGwpEl = await waitFor('#stayai-replo-rebuy-gwp', 5000).catch(() => null);
      }

      // Abort only if neither buybox nor Replo GWP container exists
      if (!stayaiCustomBuyboxEl && !reploGwpEl) {
        console.warn('>>> stayaiCustomBuyboxEl not found and no Replo GWP container');
        return;
      }
      const gwpIntegrationEnabledAttr = stayaiCustomBuyboxEl?.getAttribute('data-stayai-rebuy-gwp-integration-enabled');
      // When Replo-only, skip buybox integration check. Otherwise require integration enabled.
      if (stayaiCustomBuyboxEl && !(gwpIntegrationEnabledAttr === true || gwpIntegrationEnabledAttr === "true")) {
        console.log('>>> Rebuy GWP integration is not enabled for stayai custom buybox, aborting');
        clearGwpContainer(null);
        return;
      }
      mainHasRun = true;

      // Replo-only: use default widget ID; otherwise resolve from buybox or targetedWidgetId
      const widgetId = (reploGwpEl && !stayaiCustomBuyboxEl)
        ? REBUY_GWP_WIDGET_ID_DEFAULT
        : (targetedWidgetId != null && targetedWidgetId !== '' ? String(targetedWidgetId) : getRebuyWidgetId(stayaiCustomBuyboxEl));
      // start integration logic
      const w = await waitForRebuyWidgetReady(widgetId);
      // console.log('>>> w.data.metadata.matched_rules[0]:', w.data.metadata.matched_rules[0]);
      // console.log('>>> w.data.metadata.unmatched_rules[0]', w.data.metadata.unmatched_rules[0]);

      /* 1. get gwpData from the widget */
      const gwpData = buildVariantToGiftMap(w, widgetId);
      // console.log('>>> buildVariantToGiftMap(w) gwpData:', gwpData);

      /* 2. get current selected variant id (page may have Replo or Shopify product form) */

      let variantId = null;
      const testingTriggerVariantIdAttr = stayaiCustomBuyboxEl?.getAttribute('data-stayai-rebuy-gwp-testing-trigger-variant-id')?.trim();

      // console.log('>>> testingTriggerVariantIdAttr:', testingTriggerVariantIdAttr);

      if (testingTriggerVariantIdAttr != null && testingTriggerVariantIdAttr !== "") {
        variantId = testingTriggerVariantIdAttr;
        // console.log('>>> using testingTriggerVariantIdAttr as variantId:', variantId);
      } else {
        // console.log('>>> testingTriggerVariantIdAttr is null or empty, checking Replo container');
        const reploContainerEl = document.querySelector('[data-stayai-replo-rebuy-gwp-container]');
        const reploContainerVariant = reploContainerEl?.getAttribute('data-stayai-replo-rebuy-gwp-container')?.trim();
        if (reploContainerVariant) {
          console.log('>>> reploContainerVariant found:', reploContainerVariant);
          variantId = reploContainerVariant;
          console.log('>>> variantId from Replo container:', variantId);
        } else {
          // Single combined selector avoids two separate document scans when one form type exists
          const form = document.querySelector(SELECTOR.reploForm + ',' + SELECTOR.shopifyForm);
          if (!form) {
            console.warn('>>> No product form found (replo or shopify)');
            return;
          }
          const variantInput = form.querySelector('input[name="id"]');
          variantId = variantInput ? variantInput.value : null;
          if (variantId == null) {
            console.warn('>>> No variant id in form');
            return;
          }
          console.log('>>> current selected variantId:', variantId);
        }
      }
      /* 3. get gifts product JSON for the selected variant */
      // test fetching product data from rebuy api

      console.log('>>> variantId used to fetch gifts:', variantId);

      const gifts = giftsForVariant(gwpData, variantId);

      const giftProducts = await fetchGiftProducts({
        rebuyKey: REBUY_API_KEY,
        giftProductIds: gifts,
      });

      console.log('>>> giftProducts json:', giftProducts);

      /* 4. render the gift products */
      const isGiftDiscountValid = gwpData.discount.amount === 100 && gwpData.discount.type === 'percentage'  && gwpData.discount.quantity === 1 && gwpData.widgetType === 'gift_with_purchase'

      const giftProductsElement = document.createElement('div');
      giftProductsElement.classList.add(`${ns}-gwp-products-items-wrapper`);
      const gwpItemsHTML = (Array.isArray(giftProducts) ? giftProducts : [])
        .map((product) => `
        <div class="${ns}-gwp-product-item">
          <div class="${ns}-gwp-item-left">
            <a class="${ns}-gwp-product-image" target="_blank" href="${product?.link ?? ''}">
              <img src="${thumbnailImageUrl(product?.image?.src)}" alt="${(product?.title ?? '').replace(/"/g, '&quot;')}" width="100" loading="lazy" />
            </a>
            <div class="${ns}-gwp-product-qty">
              ${isGiftDiscountValid ? 1 : ''}
            </div>
          </div>
          <a class="${ns}-gwp-product-title" target="_blank" href="${product?.link ?? ''}">
            ${product?.title ?? ''}
          </a>
          <div class="${ns}-gwp-product-price ${ns}-gwp-item-right">
            ${isGiftDiscountValid
              ? `<span class="free-text">FREE</span> <s class="${ns}-txt-muted">&dollar;${product?.variants[0]?.price ?? 0}</s>`
              : '&dollar;'+(product?.variants[0]?.price ?? 0)
            }
          </div>
        </div>
      `)
        .join('');

      giftProductsElement.innerHTML = gwpItemsHTML;

      // Replo-only: use #stayai-replo-rebuy-gwp as container; otherwise use buybox GWP container
      const gwpContainer = (reploGwpEl && !stayaiCustomBuyboxEl)
        ? reploGwpEl
        : (stayaiCustomBuyboxEl?.querySelector(SELECTOR.gwpContainer) || SELECTOR.gwpContainerEl);

      console.log('>>> gwpContainer is:', gwpContainer);

      if (gwpContainer) {
        gwpContainer.innerHTML = '';
        gwpContainer.appendChild(giftProductsElement);
      }

      if(reploGwpEl){
        // console.log('>>> reploGwpEl found, applying styles');
        // var el = document.getElementById('stayai-custom-buybox-styles');
        // if (el) {

        //   var css = el.textContent;
        //   console.log('>>> el found, applying styles', css);
        //   var style = document.createElement('style');
        //   style.textContent = css;
        //   document.head.appendChild(style);  // or target another document, e.g. iframe
        // }
      }

    } catch (e) {
      console.error('>>>', e.message);
    }
  }

  /**
   * Waits for a DOM element matching selector to appear, then resolves with it.
   * Uses MutationObserver; cleans up observer and timeout on resolve/reject.
   * @param {string} selector - CSS selector for the element
   * @param {number} timeout - Max wait ms (default 10000)
   * @param {Element} root - Root to observe (default document.documentElement)
   * @returns {Promise<Element>}
   */
  function waitFor(selector, timeout = 10000, root = document.documentElement) {
    return new Promise((resolve, reject) => {
      const foundNow = root.querySelector(selector);
      if (foundNow) return resolve(foundNow);

      let settled = false;
      const mo = new MutationObserver(() => {
        try {
          const el = root.querySelector(selector);
          if (el) done(null, el);
        } catch (e) {
          done(e);
        }
      });

      let timer = setTimeout(() => {
        done(new Error('timeout waiting for ' + selector));
      }, timeout);

      function done(err, el) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        mo.disconnect();
        err ? reject(err) : resolve(el);
      }

      try {
        mo.observe(root, { childList: true, subtree: true });
      } catch (e) {
        done(e);
      }
    });
  }


  // Run main when Rebuy loads (pass a function so main runs on event, not at registration)
  document.addEventListener('rebuy.loaded', () => main());
  // If Rebuy is already loaded, run once now; mainHasRun guard prevents double run
  if (window.Rebuy?.widgets) main();
})();